/* ============================================================================
   Request-detail TAB registry.
   The Requests module detail page renders whatever tabs are registered here.
   Feature modules (Proofing, Hours, Comments, ...) register their own tab from
   their own files — no edits to the Requests module or any shared file needed.
   ============================================================================ */

import type { ComponentType } from 'react';
import type { Role } from '../lib/auth';

/** Props every request-detail tab component receives. */
export interface RequestTabProps {
  /** The request/ticket id from the route. */
  requestId: number;
}

export interface RequestTabSpec {
  /** Stable unique id, e.g. "overview", "proofing", "hours". */
  id: string;
  /** Tab label. */
  label: string;
  /** Lower numbers sort earlier. Default 100. */
  order?: number;
  /** If set, only these roles see the tab. */
  roles?: Role[] | string[];
  /** Rendered when the tab is active. */
  component: ComponentType<RequestTabProps>;
}

const tabs: RequestTabSpec[] = [];

export function registerRequestTab(spec: RequestTabSpec): void {
  const idx = tabs.findIndex((t) => t.id === spec.id);
  if (idx >= 0) tabs.splice(idx, 1);
  tabs.push(spec);
}

/** Tabs sorted by (order, label). */
export function getRequestTabs(): RequestTabSpec[] {
  return tabs
    .slice()
    .sort((a, b) => {
      const oa = a.order ?? 100;
      const ob = b.order ?? 100;
      if (oa !== ob) return oa - ob;
      return a.label.localeCompare(b.label);
    });
}
